import { useMemo } from 'react';
import { startOfWeek, endOfWeek, subWeeks } from 'date-fns';

/**
 * Rule-based financial insights engine.
 *
 * Derives smart nudges purely from already-fetched transaction data —
 * no extra network calls. Returns an array of insight objects:
 *   { id, text, type: 'success'|'warning'|'info', priority: number }
 *
 * Insights are sorted by priority (lower = more urgent / shown first).
 */
export function useInsights({
  allTransactions = [],
  monthlyTransactions = [],
  salaryThisMonth = 0,
  expensesThisMonth = 0,
  carryForward = 0,
  ageOfMoney = 0,
  ageOfMoneyStatus = 'no-data',
  totalAllocated = 0,
  toBeBudgeted = 0,
  budgetCategories = [],
  selectedDate = new Date(),
}) {
  return useMemo(() => {
    const insights = [];
    const today = new Date();
    const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
    const dayOfMonth = selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear()
      ? today.getDate()
      : daysInMonth;

    const fmt = (v) =>
      new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v || 0));

    // ── 1. AGE OF MONEY ───────────────────────────────────────────────
    if (ageOfMoneyStatus === 'on-track' && ageOfMoney > 0) {
      insights.push({
        id: 'age-on-track',
        text: `Your money is ${ageOfMoney} days old — healthy buffer. You're not living paycheck to paycheck.`,
        type: 'success',
        priority: 2
      });
    } else if (ageOfMoneyStatus === 'behind' && ageOfMoney > 0) {
      insights.push({
        id: 'age-behind',
        text: `Age of money: ${ageOfMoney} days (goal: 30). Try to spend from last month's income, not this month's.`,
        type: 'warning',
        priority: 1
      });
    }

    // ── 2. UNALLOCATED BUDGET ─────────────────────────────────────────
    if (toBeBudgeted > 0 && totalAllocated > 0) {
      insights.push({
        id: 'unallocated',
        text: `${fmt(toBeBudgeted)} is unallocated. Give every rupee a job before month end.`,
        type: 'warning',
        priority: 1
      });
    } else if (toBeBudgeted === 0 && totalAllocated > 0) {
      insights.push({
        id: 'fully-allocated',
        text: 'Budget fully allocated — every rupee has a job.',
        type: 'success',
        priority: 4
      });
    }

    // ── 3. OVER-BUDGET ENVELOPES ──────────────────────────────────────
    const overBudget = budgetCategories.filter(
      c => c.spent_amount > c.allocated_amount && c.allocated_amount > 0
    );
    if (overBudget.length > 0) {
      const names = overBudget.slice(0, 2).map(c => c.category?.name || 'a category').join(', ');
      insights.push({
        id: 'over-budget',
        text: `${overBudget.length} envelope${overBudget.length > 1 ? 's' : ''} over budget: ${names}.`,
        type: 'warning',
        priority: 0
      });
    }

    // ── 4. SPENDING PACE ──────────────────────────────────────────────
    if (expensesThisMonth > 0 && dayOfMonth > 0) {
      const dailyAvg = expensesThisMonth / dayOfMonth;
      const projectedTotal = dailyAvg * daysInMonth;
      const totalIncome = carryForward + salaryThisMonth;

      if (totalIncome > 0 && projectedTotal > totalIncome * 1.1) {
        insights.push({
          id: 'overspend-pace',
          text: `At ${fmt(dailyAvg)}/day you're on pace to spend ${fmt(projectedTotal)} — that's more than your income.`,
          type: 'warning',
          priority: 0
        });
      } else if (totalIncome > 0) {
        insights.push({
          id: 'spend-pace',
          text: `Spending at ${fmt(Math.round(dailyAvg))}/day — projected ${fmt(Math.round(projectedTotal))} for the month.`,
          type: 'info',
          priority: 3
        });
      }
    }

    // ── 5. CARRY FORWARD ─────────────────────────────────────────────
    if (carryForward > 0) {
      insights.push({
        id: 'carry-forward',
        text: `You carried ${fmt(carryForward)} forward from previous months — that's your buffer.`,
        type: 'success',
        priority: 3
      });
    }

    // ── 6. TOP SPENDING CATEGORY ──────────────────────────────────────
    const categoryTotals = monthlyTransactions
      .filter(t => Number(t.debit || 0) > 0)
      .reduce((acc, t) => {
        const name = t.categories?.name || 'Other';
        acc[name] = (acc[name] || 0) + Number(t.debit);
        return acc;
      }, {});

    const topCat = Object.entries(categoryTotals).sort(([, a], [, b]) => b - a)[0];
    if (topCat && expensesThisMonth > 0) {
      const [catName, catTotal] = topCat;
      const pct = Math.round((catTotal / expensesThisMonth) * 100);
      if (pct >= 30) {
        insights.push({
          id: 'top-category',
          text: `${catName} is your biggest expense this month — ${fmt(catTotal)} (${pct}% of total spending).`,
          type: 'info',
          priority: 3
        });
      }
    }

    // ── 7. WEEK-OVER-WEEK COMPARISON ─────────────────────────────────
    const thisWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    const thisWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
    const lastWeekStart = subWeeks(thisWeekStart, 1);
    const lastWeekEnd = subWeeks(thisWeekEnd, 1);

    const thisWeekSpend = allTransactions
      .filter(t => {
        const d = new Date(t.date);
        return Number(t.debit || 0) > 0 && d >= thisWeekStart && d <= thisWeekEnd;
      })
      .reduce((sum, t) => sum + Number(t.debit), 0);

    const lastWeekSpend = allTransactions
      .filter(t => {
        const d = new Date(t.date);
        return Number(t.debit || 0) > 0 && d >= lastWeekStart && d <= lastWeekEnd;
      })
      .reduce((sum, t) => sum + Number(t.debit), 0);

    if (thisWeekSpend > 0 && lastWeekSpend > 0) {
      const diff = thisWeekSpend - lastWeekSpend;
      const diffPct = Math.abs(Math.round((diff / lastWeekSpend) * 100));
      if (diffPct >= 15) {
        if (diff > 0) {
          insights.push({
            id: 'week-up',
            text: `Spending is up ${diffPct}% vs last week (${fmt(thisWeekSpend)} vs ${fmt(lastWeekSpend)}).`,
            type: 'warning',
            priority: 2
          });
        } else {
          insights.push({
            id: 'week-down',
            text: `Spending down ${diffPct}% vs last week — good discipline this week.`,
            type: 'success',
            priority: 3
          });
        }
      }
    }

    // ── 8. NO INCOME RECORDED ────────────────────────────────────────
    if (salaryThisMonth === 0 && carryForward === 0 && expensesThisMonth > 0) {
      insights.push({
        id: 'no-income',
        text: 'No income recorded this month. Add your salary so the budget can work correctly.',
        type: 'warning',
        priority: 0
      });
    }

    // Sort by priority (ascending = most urgent first)
    return insights.sort((a, b) => a.priority - b.priority).slice(0, 4);
  }, [
    allTransactions, monthlyTransactions, salaryThisMonth, expensesThisMonth,
    carryForward, ageOfMoney, ageOfMoneyStatus, totalAllocated, toBeBudgeted,
    budgetCategories, selectedDate
  ]);
}
